import type { CustomValueWarningField } from '@aurora/shared-types/customValueWarning';
import type { Range } from '@aurora/shared-types/range';
import type { ChangeEvent } from 'react';
import React, { useRef } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import type { FieldValues } from 'react-hook-form';
import type { FieldPath } from 'react-hook-form/dist/types/path';
import { isWithinRange } from '../../../helpers/range/RangeHelper';
import ComponentHelper from '../../../helpers/ui/ComponentHelper/ComponentHelper';
import SizedContainer from '../../common/SizedContainer/SizedContainer';
import CustomValueWarningWrapper from '../CustomValueWarning/CustomValueWarningWrapper';
import type { FormFieldVariant } from '../enums';
import FormField from '../FormField/FormField';
import { useSubmitOnChange } from '../FormField/useSubmitOnChange';
import type { FormFieldProps, FormFieldType } from '../types';
import { FieldType } from '../types';
import localStyles from './RangeField.module.pcss';

/**
 * Range Field definition to render the range field. NameT is the name of the field and DataT is the definition of bean
 * that holds all the fields for the form.
 */
export interface RangeFieldSpec<NameT extends FieldPath<DataT>, DataT extends FieldValues>
  extends Range,
    CustomValueWarningField<number>,
    Omit<FormFieldType<NameT, DataT, FormFieldVariant.RANGE>, 'onChange' | 'value'> {
  /**
   * The stepping interval.
   */
  step?: number;
  /**
   * Whether ot not to display the current value alongside the range.
   */
  showOutput?: boolean;
  /**
   * Allows for the addition of an output override for special values, controlled by external logic
   */
  outputOverride?: (value: number) => string;
  /**
   * Allows for the addition of a unit to the displayed value (such as %).
   */
  outputSuffix?: string;
  /**
   * Sets the width of the range input in px
   */
  inputWidth?: number;
  /**
   * Class name(s) to apply to the input wrapper element.
   */
  wrapClassName?: string;
}

/**
 * Returns styles used for styling the range input track and width
 *
 * @param min the min value
 * @param max the max value
 * @param defaultValue the default value
 * @param inputWidth the width (in px) for the range input
 */
export function getRangeStyles(
  min: number,
  max: number,
  defaultValue: number,
  inputWidth?: number
): React.CSSProperties {
  const midPoint = (max - min) / 2;
  const percentage = ((defaultValue - min) / (max - min)) * 100;
  const adjustedPercentage = percentage > midPoint ? Math.floor(percentage) : percentage;

  return {
    background: `
      linear-gradient(to right,
        var(--lia-input-active-fill-color) 0%,
        var(--lia-input-active-fill-color) ${adjustedPercentage}%,
        var(--lia-bs-gray-400) ${adjustedPercentage}%,
        var(--lia-bs-gray-400) 100%)`,
    ...(!!inputWidth && { width: `${inputWidth}px` })
  };
}
/**
 * Updates the current value for the input
 *
 * @param event the change event
 * @param onChange a react-hook-form callback function to update the form value
 */
function handleChange(event, onChange): void {
  const updatedValue: number = +event.target.value;
  onChange(updatedValue);
}

/**
 * Renders a customized range input
 *
 * @constructor
 *
 * @author Jonathan Bridges
 */
const RangeField = <NameT extends FieldPath<DataT>, DataT extends FieldValues>({
  className,
  fieldSpec,
  formId,
  i18n,
  formMethods,
  onFormSubmit,
  submitOnChange,
  allFields
}: FormFieldProps<NameT, DataT, RangeFieldSpec<NameT, DataT>>): React.ReactElement => {
  const cx = useClassNameMapper(localStyles);
  const {
    customValueWarningOptions,
    name,
    min,
    max,
    step,
    showOutput,
    outputSuffix,
    focus,
    focusHandler,
    disabled,
    outputOverride,
    inputWidth,
    wrapClassName
  } = fieldSpec;
  const hasBeenFocused = useRef(false);

  /**
   * Renders the range input's value as <output> with an optional suffix (e.g. %)
   *
   * @param value the value of the range input
   */
  function getSuffixedOutput(value: number): React.ReactElement {
    return (
      <div className={cx('lia-range-output')}>
        <output>{value}</output>
        {outputSuffix && <span>{outputSuffix}</span>}
      </div>
    );
  }

  /**
   * Renders the range input's value using either an override function or the #getSuffixedOutput function provided above
   *
   * @param value the value of the range input
   */
  function getOutput(value: number): React.ReactElement {
    const overridden: string = outputOverride && outputOverride(value);

    if (overridden) {
      return <output className={cx('lia-range-output')}>{overridden}</output>;
    }

    if (showOutput) {
      return getSuffixedOutput(value);
    }

    return null;
  }
  const onValueChange = useSubmitOnChange(name, submitOnChange, onFormSubmit);

  return (
    <FormField
      fieldSpec={fieldSpec}
      fieldType={FieldType.CONTROLLED}
      formId={formId}
      i18n={i18n}
      formMethods={formMethods}
      onFormSubmit={onFormSubmit}
      submitOnChange={undefined}
      allFormFields={allFields}
      className={cx(className)}
    >
      {({
        render: {
          field: { value, onChange, onBlur }
        }
      }) => {
        return (
          <>
            <CustomValueWarningWrapper
              options={customValueWarningOptions}
              defaultLogic={currentValue => !isWithinRange(currentValue, { min, max })}
              value={value}
            />
            <div className={cx(wrapClassName, 'lia-range-wrapper')}>
              <input
                aria-label={name}
                type="range"
                value={value}
                min={min}
                max={max}
                step={step}
                disabled={disabled}
                onChange={(event: ChangeEvent<HTMLInputElement>) => handleChange(event, onChange)}
                onMouseUp={(event: React.MouseEvent<HTMLInputElement>) => {
                  const target = event.target as HTMLInputElement;
                  onValueChange(target.value as DataT[NameT]);
                }}
                onBlur={onBlur}
                className={cx('lia-range', { 'lia-g-mr-10': showOutput })}
                style={getRangeStyles(min, max, value, inputWidth)}
                ref={(element): void => {
                  if (focus) {
                    ComponentHelper.focusHandler(hasBeenFocused, element, focusHandler);
                  }
                }}
                data-testid={`RangeField.${formId}.${name}`}
              />
              <SizedContainer sizer={getSuffixedOutput(max)} className={cx('d-flex flex-shrink-0')}>
                {getOutput(value)}
              </SizedContainer>
            </div>
          </>
        );
      }}
    </FormField>
  );
};
export default RangeField;
